<script setup lang="ts">
/**
 * PageShell — FO 페이지 공통 레이아웃 셸(컨테이너 + 상·하단 패딩). 여백·폭의 단일 책임자(SSoT).
 *
 * 페이지마다 복붙되던 `mx-auto max-w-page px-[18px] md:px-8 xl:px-10` 컨테이너와
 * 제각각이던 상단 패딩(pt-6 / pt-10 / py-12 …)을 한 곳에서 고정한다. 페이지는 내용만 slot 에 넣고,
 * 폭·여백은 일절 지정하지 않는다. 수치는 tailwind.config.ts 시맨틱 토큰(max-w-page*, page-top/bottom)이 SSoT.
 *
 * <b>상단 여백은 커머스 전 페이지 단일 기준(page-top=40px)</b> — 유형이 달라도 콘텐츠 시작 높이를 일치시킨다.
 * 예외 2종만 압축(page-top-dense=24px):
 * - `tool`(탐색): 헤더 직하단에 sticky 도구바가 곧바로 붙는 구조라 기능적 필연.
 * - `workspace`: 워크스페이스 GNB(border-b) + 브레드크럼(mb-4) + 이미지 배너(PageBanner)가 쌓여
 *   40px 이면 GNB→본문 텍스트가 과하게 벌어진다(2026-09-18 정시 배너 적용 후 조정).
 * 따라서 variant 는 사실상 (1) 컨테이너 폭, (2) 상단 압축 여부 두 가지만 가른다.
 *
 * - tool      : 탐색/도구형(Materials) — 상단 압축(sticky 도구바). 폭 standard.
 * - list      : 결제/목록형(Packages·Checkout) — 표준 여백. 폭 standard.
 * - mypage    : 마이페이지형(자료실·구매내역·문의 …) — 표준 여백. 폭 standard(상세는 width=detail).
 * - legal     : 약관·법률 문서(Terms·Privacy·Business) — 표준 여백. 폭 legal(좁게).
 * - workspace : 2차 AI 분석 워크스페이스(analysis/*) — 상단 압축(24px). 폭 workspace.
 *
 * 폭은 커머스·워크스페이스 모두 1440 로 통일했다(구 1200/1360). 좁게 두는 건 <b>읽기 화면</b>뿐 —
 * 약관(legal 780)·상세 1열(detail 820)은 한 줄이 길면 못 읽으므로 의도적으로 제한한다.
 *
 * 본문은 default slot. 타이틀 밴드는 PageTitle(단일 컴포넌트)을 slot 최상단에 둔다(셸은 패딩만, 밴드는 pt 없음).
 * 제외 페이지(랜딩·인증·결과 카드 등)는 셸 개념이 달라 감싸지 않는다 — 각 페이지 주석 참조.
 */
import { computed } from 'vue';

type Variant = 'tool' | 'list' | 'mypage' | 'legal' | 'workspace';
type Width = 'standard' | 'detail' | 'legal' | 'workspace';

interface Props {
  /** 페이지 유형 — 폭 + 상단 압축 여부 프리셋(tool·workspace 만 압축, 그 외 동일). */
  variant?: Variant;
  /**
   * 컨테이너 폭 오버라이드(선택). 미지정 시 variant 기본(legal=legal폭, 그 외=standard).
   * detail=820px 상세 1열(예: 문의 상세) — 마이페이지형이라도 가독 폭으로 좁힐 때.
   */
  width?: Width;
  /** 최소 높이 부여(짧은 본문에서 푸터가 치고 올라오는 것 방지). 기본 tool 외 true. */
  minHeight?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'list',
  width: undefined,
  minHeight: undefined,
});

/** 폭 토큰 — width prop 우선, 없으면 variant 기본(legal=좁게, workspace=넓게, 그 외 표준). */
const widthClass = computed(() => {
  const resolved =
    props.width ??
    (props.variant === 'legal'
      ? 'legal'
      : props.variant === 'workspace'
        ? 'workspace'
        : 'standard');
  return {
    standard: 'max-w-page',
    detail: 'max-w-page-detail',
    legal: 'max-w-page-legal',
    workspace: 'max-w-page-workspace',
  }[resolved];
});

/**
 * 상·하단 패딩 — 상단 여백은 커머스 단일 기준(page-top=40px). tool(sticky 도구바)·workspace(GNB+브레드크럼+배너 적층)만 압축.
 * 하단은 모든 유형 page-bottom(72px) 통일.
 */
const paddingClass = computed(() => {
  if (props.variant === 'tool') return 'pt-page-top-dense md:pt-7 pb-page-bottom'; // 탐색: 상단 압축(24→md 28)
  if (props.variant === 'workspace') return 'pt-page-top-dense pb-page-bottom'; // 워크스페이스: 상단 24
  return 'pt-page-top pb-page-bottom'; // 그 외: 상단 40, 하단 72 — 단일 기준
});

/** minHeight 기본값 — tool 은 false(도구바가 채움), 그 외 true. */
const showMinHeight = computed(() =>
  props.minHeight === undefined ? props.variant !== 'tool' : props.minHeight,
);
</script>

<template>
  <section :class="showMinHeight ? 'min-h-[60vh]' : ''">
    <div :class="['mx-auto w-full px-[18px] md:px-8 xl:px-10', widthClass, paddingClass]">
      <slot />
    </div>
  </section>
</template>
